import type { ComponentChildren } from 'preact';
import { useEffect } from 'preact/hooks';
import { useI18n } from '../i18n';
import { useAuth } from '../lib/auth';

/// Gate for /admin routes. Renders [children] when the current session has
/// role=admin; redirects to the login page (with a return-to query) otherwise.
/// While the auth provider is still hydrating, shows a thin loading state so
/// we don't briefly flash the unauthorised redirect on every cold load.
///
/// Partners who land here (typically via a stale `?return=/admin` after the
/// login round-trip) are silently bounced to /partner — their actual home —
/// rather than being shown a harsh "Not authorised" wall.
export function RequireAdmin({ children }: { children: ComponentChildren }) {
  const { status, user } = useAuth();
  const { t, localePath } = useI18n();

  useEffect(() => {
    if (status === 'signed-out') {
      const ret = encodeURIComponent(location.pathname + location.search);
      location.replace(`${localePath('/login')}?return=${ret}`);
    } else if (status === 'signed-in' && user?.role === 'partner') {
      location.replace(localePath('/partner'));
    }
  }, [status, user]);

  if (status === 'unknown') {
    return (
      <div class="admin-page">
        <div class="admin-shell">
          <p class="admin-lede">{t('admin.gate.checking')}</p>
        </div>
      </div>
    );
  }
  if (status === 'signed-out' || user?.role === 'partner') {
    return null;
  }
  if (user?.role !== 'admin') {
    return (
      <div class="admin-page">
        <div class="admin-shell">
          <h1>{t('admin.gate.not_authorised_title')}</h1>
          <p class="admin-lede">
            {t('admin.gate.not_authorised_body').replace('{email}', user?.email ?? '')
              .replace('{role}', user?.role ?? '')}
          </p>
          <a class="admin-back" href={localePath()}>{t('admin.gate.back')}</a>
        </div>
      </div>
    );
  }
  return <>{children}</>;
}
